import { useState, useEffect, useCallback } from 'react';
import { provincesService, type Province, type District } from '../services/provincesService';

export const useProvinces = () => {
  const [provinces, setProvinces] = useState<Province[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const [isLoadingProvinces, setIsLoadingProvinces] = useState(false);
  const [isLoadingDistricts, setIsLoadingDistricts] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load provinces on mount
  useEffect(() => {
    loadProvinces();
  }, []);

  const loadProvinces = useCallback(async () => {
    setIsLoadingProvinces(true);
    setError(null);
    
    try {
      const data = await provincesService.getProvinces();
      setProvinces(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Lỗi không xác định');
    } finally {
      setIsLoadingProvinces(false);
    }
  }, []);

  const loadDistrictsByProvince = useCallback(async (provinceCode: number) => {
    if (!provinceCode) {
      setDistricts([]);
      return;
    }

    setIsLoadingDistricts(true);
    setError(null);
    
    try {
      const data = await provincesService.getDistrictsByProvinceCode(provinceCode);
      setDistricts(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Lỗi không xác định');
    } finally {
      setIsLoadingDistricts(false);
    }
  }, []);

  const searchProvinces = useCallback(async (query: string) => {
    if (!query.trim()) {
      return provinces;
    }

    try {
      const results = await provincesService.searchProvinces(query);
      return results;
    } catch (err) {
      console.error('Error searching provinces:', err);
      return provinces;
    }
  }, [provinces]);

  const searchDistricts = useCallback(async (query: string, provinceCode?: number) => {
    if (!query.trim()) {
      return districts;
    }

    try {
      const results = await provincesService.searchDistricts(query);
      // Filter by province if specified
      if (provinceCode) {
        return results.filter(district => district.province_code === provinceCode);
      }
      return results;
    } catch (err) {
      console.error('Error searching districts:', err);
      return districts;
    }
  }, [districts]);

  const getProvinceByCode = useCallback((code: number) => {
    return provinces.find(p => p.code === code);
  }, [provinces]);

  const getDistrictByCode = useCallback((code: number) => {
    return districts.find(d => d.code === code);
  }, [districts]);

  const clearDistricts = useCallback(() => {
    setDistricts([]);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // Data
    provinces,
    districts,
    
    // Loading states
    isLoadingProvinces,
    isLoadingDistricts,
    
    // Error state
    error,
    
    // Actions
    loadProvinces,
    loadDistrictsByProvince,
    searchProvinces,
    searchDistricts,
    getProvinceByCode,
    getDistrictByCode,
    clearDistricts,
    clearError,
  };
};
